import React from 'react';
import type { AgentArtifact, AgentRunStatus, InboundEmailStatus } from '../../types/agent';

const RUN_STATUS: Record<AgentRunStatus, { label: string; cls: string }> = {
  pending: { label: 'Queued', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  running: { label: 'Running', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  awaiting_approval: { label: 'Needs approval', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  completed: { label: 'Completed', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' },
  completed_with_errors: { label: 'Completed with errors', cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

const EMAIL_STATUS: Record<InboundEmailStatus, { label: string; cls: string }> = {
  received: { label: 'Received', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  queued: { label: 'Queued', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  processing: { label: 'Processing', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  completed: { label: 'Handled', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
  quarantined: { label: 'Quarantined', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  discarded: { label: 'Discarded', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
};

export const RunStatusChip: React.FC<{ status: AgentRunStatus; mode?: string }> = ({ status, mode }) => {
  const s = RUN_STATUS[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
      {mode === 'shadow' && <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200">Shadow</span>}
    </span>
  );
};

export const EmailStatusChip: React.FC<{ status: InboundEmailStatus }> = ({ status }) => {
  const s = EMAIL_STATUS[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
};

export const REJECT_REASONS: Record<string, string> = {
  unknown_sender: 'Sender is not a RiskNinja user',
  wrong_company: 'Sender belongs to a different company',
  inactive_user: 'Sender account is not active',
  company_locked: 'Company account on hold',
  authentication_failed: 'Failed SPF/DKIM/DMARC checks',
  auto_generated: 'Auto-reply / auto-generated',
  bulk: 'Bulk or mailing-list mail',
  self_sender: 'Sent by the agent mailbox itself',
  own_reply_echo: 'Echo of our own email',
  rate_limited: 'Sender over the hourly limit',
  agent_not_enabled: 'Agent not enabled for the company',
  daily_cap: 'Company daily run cap reached',
  no_from: 'No sender address',
};

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function fmtDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '';
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 100) / 10;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

export function fmtTokens(n: number | undefined): string {
  if (!n) return '0';
  return n >= 1000 ? `${Math.round(n / 100) / 10}k` : String(n);
}

export const ARTIFACT_LABEL: Record<string, string> = {
  report: 'Report',
  document: 'Document',
  customer: 'Customer',
  folder: 'Folder',
  acord_form: 'ACORD form',
  filled_template: 'Filled template',
  certificate: 'Certificate',
  email_draft: 'Email draft',
  pdf: 'PDF',
  workflow_job: 'Workflow run',
  writeup: 'Write-up',
};

export const ARTIFACT_ICON: Record<string, string> = {
  report: '📊',
  document: '📄',
  customer: '🏢',
  folder: '📁',
  acord_form: '🧾',
  filled_template: '📝',
  certificate: '📜',
  email_draft: '✉️',
  pdf: '📕',
  workflow_job: '⚙️',
  writeup: '📝',
};

/** In-app route for an artifact, or null when it opens in place (drafts, write-ups, files). */
export function artifactRoute(a: AgentArtifact): string | null {
  if (a.type === 'report' && a.refId) return `/workflow-report?reportId=${a.refId}`;
  if (a.type === 'folder' && a.customerId && a.refId) return `/customers/${a.customerId}/projects/${a.refId}?tab=documents`;
  if ((a.type === 'document' || a.type === 'acord_form' || a.type === 'filled_template' || a.type === 'certificate') && a.customerId && a.projectId) return `/customers/${a.customerId}/projects/${a.projectId}?tab=documents`;
  if (a.type === 'customer' && a.refId) return `/customers/${a.refId}`;
  if (a.type === 'workflow_job' && a.customerId && a.projectId) return `/customers/${a.customerId}/projects/${a.projectId}?tab=reports`;
  return null;
}

export const Spinner: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={`animate-spin text-primary ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

export const EmptyState: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
    <p className="text-gray-700 dark:text-gray-200 font-medium">{title}</p>
    {children && <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">{children}</div>}
  </div>
);

export const btn = {
  primary: 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50',
  secondary: 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50',
  danger: 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-red-300 text-red-700 dark:text-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50',
  success: 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50',
};

export const card = 'bg-white dark:bg-dark-surface border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100';
export const input = 'w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40';
